import { useMemo, useState } from "react";
import { Activity, Clock3, Filter, RefreshCw, UserRound } from "lucide-react";
import type { AdminAccessLogRecord } from "@/types/accessLogs";
import { Button, EmptyState, StatusBadge } from "@/ui/components";

type RecentAccessLogPaneProps = {
  logs: AdminAccessLogRecord[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  tone: "ifc" | "project-files";
  subtitle: string;
};

const ACTION_LABELS: Record<string, string> = {
  DEBUG_SESSION_CREATED: "Debug viewer session",
  DOWNLOAD_URL_CREATED: "Downloaded project file",
  IFC_VIEWER_SESSION_CREATED: "Opened IFC from portal",
  OTP_VERIFIED: "Verified email access",
  PROJECT_FILE_SESSION_VIEWER_CREATED: "Opened model from portal",
  SUCCESS: "Opened IFC viewer",
};

type AccessActionFilter = "all" | "verified-email" | "download-file" | "open-ifc";

const ACTION_FILTER_OPTIONS: Array<{ value: AccessActionFilter; label: string }> = [
  { value: "all", label: "All actions" },
  { value: "verified-email", label: "Verified email" },
  { value: "download-file", label: "Downloaded file" },
  { value: "open-ifc", label: "Opened IFC" },
];

const FILTERED_RESULTS: Record<Exclude<AccessActionFilter, "all">, Set<string>> = {
  "verified-email": new Set(["OTP_VERIFIED"]),
  "download-file": new Set(["DOWNLOAD_URL_CREATED"]),
  "open-ifc": new Set([
    "DEBUG_SESSION_CREATED",
    "IFC_VIEWER_SESSION_CREATED",
    "PROJECT_FILE_SESSION_VIEWER_CREATED",
    "SUCCESS",
  ]),
};

function humanizeResult(result: string) {
  const normalized = String(result || "Activity").trim().toUpperCase();
  return ACTION_LABELS[normalized]
    || normalized.toLowerCase().replace(/_/g, " ").replace(/^./, (value) => value.toUpperCase());
}

function isFailureResult(result: string) {
  return /(FAILED|ERROR|EXPIRED|INVALID|NOT_FOUND|REVOKED|EXCEEDED|LIMIT)/i.test(result);
}

function formatAccessTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "Unknown time";
  return `${date.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Australia/Melbourne",
  })} Melbourne`;
}

function getResourceName(log: AdminAccessLogRecord): string | null {
  if (log.fileName) return log.fileName;
  if (log.resourceLabel) return log.resourceLabel;
  if (log.projectLabel) return null;
  if (log.projectNumber) return `Project ${log.projectNumber}`;
  if (log.projectId) return `Project ${log.projectId}`;
  return "Portal access";
}

export default function RecentAccessLogPane({
  logs,
  loading,
  error,
  onRefresh,
  tone,
  subtitle,
}: RecentAccessLogPaneProps) {
  const [actionFilter, setActionFilter] = useState<AccessActionFilter>("all");
  const filteredLogs = useMemo(() => {
    if (actionFilter === "all") return logs;
    const acceptedResults = FILTERED_RESULTS[actionFilter];
    return logs.filter((log) => acceptedResults.has(String(log.result || "").trim().toUpperCase()));
  }, [actionFilter, logs]);

  return (
    <aside className={`recent-access-pane recent-access-pane--${tone}`} aria-label="Recent access log">
      <div className="recent-access-pane__header">
        <div>
          <h3>Recent access</h3>
          <p>{subtitle}</p>
        </div>
        <StatusBadge status={String(filteredLogs.length)}>
          {actionFilter === "all" ? logs.length : `${filteredLogs.length}/${logs.length}`}
        </StatusBadge>
      </div>

      <div className="recent-access-pane__controls">
        <label className="recent-access-pane__filter">
          <Filter size={15} aria-hidden="true" />
          <select
            aria-label="Filter by action type"
            value={actionFilter}
            onChange={(event) => setActionFilter(event.target.value as AccessActionFilter)}
          >
            {ACTION_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          iconBefore={<RefreshCw size={15} aria-hidden="true" />}
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? "Refreshing" : "Refresh"}
        </Button>
      </div>

      {error ? <p className="recent-access-pane__error">{error}</p> : null}

      {filteredLogs.length ? (
        <div className="recent-access-pane__list">
          {filteredLogs.map((log) => {
            const resourceName = getResourceName(log);
            return (
              <article key={log.id} className="recent-access-item">
                <div className="recent-access-item__person">
                  <UserRound size={15} aria-hidden="true" />
                  <strong title={log.requesterEmail || "Unknown visitor"}>{log.requesterEmail || "Unknown visitor"}</strong>
                </div>
                <div className="recent-access-item__time">
                  <Clock3 size={14} aria-hidden="true" />
                  <time dateTime={log.accessedAt}>{formatAccessTime(log.accessedAt)}</time>
                </div>
                <div className={`recent-access-item__action${isFailureResult(log.result) ? " recent-access-item__action--error" : ""}`}>
                  <Activity size={14} aria-hidden="true" />
                  <span>{humanizeResult(log.result)}</span>
                </div>
                {resourceName ? <strong className="recent-access-item__resource">{resourceName}</strong> : null}
                {log.resourceLabel || log.projectLabel ? (
                  <span className="recent-access-item__context">
                    {[log.projectNumber ? `Project ${log.projectNumber}` : null, log.resourceLabel, log.projectLabel]
                      .filter((value, index, values) => value && values.indexOf(value) === index)
                      .join(" · ")}
                  </span>
                ) : null}
                {log.message && log.message !== log.fileName ? <small>{log.message}</small> : null}
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<Activity size={26} />}
          title={loading ? "Loading recent access" : logs.length ? "No matching activity" : "No access recorded"}
          description={logs.length ? "Choose another action type to see other recent activity." : "Verified portal and viewer activity will appear here."}
        />
      )}
    </aside>
  );
}
